/*
 * NoiseClassifier — shared noise-type classifier for microphone diagnostic tools.
 *
 * Extracted verbatim (behaviour-identical) from mic-noise-test.blade.php so that
 * mic-test and mic-noise-test share ONE implementation instead of two that drift.
 * Operates on an averaged magnitude spectrum in dB (as returned by
 * AnalyserNode.getFloatFrequencyData). All detectors are pure functions.
 *
 * UMD-lite footer: assigns window.NoiseClassifier in the browser AND
 * module.exports in Node, so the Node regression harness tests the real code.
 */
(function (root) {
    'use strict';

    /* dB-domain average over [from,to] bins, skipping empty (-Infinity) bins. */
    function avgRange(arr, from, to) {
        var s = 0, c = 0;
        from = Math.max(0, from);
        to = Math.min(arr.length - 1, to);
        for (var i = from; i <= to; i++) {
            if (arr[i] !== -Infinity) { s += arr[i]; c++; }
        }
        return c > 0 ? s / c : -100;
    }

    /* Mains hum: sum of first 6 harmonics of baseFreq (50/60 Hz) vs their
       ±4-bin neighbours. Selective — a single stray peak won't trigger it. */
    function detectHum(spectrum, binRes, baseFreq) {
        /* Neighbour offset is FREQUENCY-based (~24 Hz), not a fixed ±4 bins. At
           mic-noise-test's 8192-FFT@48k this is 4 bins (identical to the original);
           at a coarser FFT (e.g. 4096) it stays ~2 bins so the reference neighbour
           does not accidentally land on the NEXT 50/60 Hz harmonic and cancel the
           peak. Makes hum detection correct at any FFT size. */
        var off = Math.max(2, Math.round(24 / binRes));
        var total = 0;
        for (var h = 1; h <= 6; h++) {
            var bin = Math.round(baseFreq * h / binRes);
            if (bin >= spectrum.length) break;
            var peak = Math.max(
                spectrum[Math.max(0, bin - 1)],
                spectrum[bin],
                spectrum[Math.min(spectrum.length - 1, bin + 1)]
            );
            var left = spectrum[Math.max(0, bin - off)];
            var right = spectrum[Math.min(spectrum.length - 1, bin + off)];
            total += peak - (left + right) / 2;
        }
        return total / 6;
    }

    /* Preamp hiss: HF band (4–16 kHz) energy above mid band (200–2000 Hz). */
    function detectHiss(spectrum, binRes) {
        var highAvg = avgRange(spectrum, Math.round(4000 / binRes), Math.round(16000 / binRes));
        var midAvg = avgRange(spectrum, Math.round(200 / binRes), Math.round(2000 / binRes));
        return highAvg - midAvg;
    }

    /* Low-frequency rumble: LF band (20–80 Hz) above low-mid (200–500 Hz). */
    function detectRumble(spectrum, binRes) {
        var lowAvg = avgRange(spectrum, Math.round(20 / binRes), Math.round(80 / binRes));
        var midAvg = avgRange(spectrum, Math.round(200 / binRes), Math.round(500 / binRes));
        return lowAvg - midAvg;
    }

    /* Is `freq` within 8 Hz of a 50 Hz or 60 Hz harmonic (so EMI ignores hum). */
    function isHumHarmonic(freq) {
        for (var h = 1; h <= 7; h++) {
            if (Math.abs(freq - 50 * h) < 8) return true;
            if (Math.abs(freq - 60 * h) < 8) return true;
        }
        return false;
    }

    /* Electromagnetic whine: sharpest non-hum peak above its local neighbours. */
    function detectEmi(spectrum, binRes) {
        var maxPeak = 0;
        var startBin = Math.round(100 / binRes);
        var endBin = Math.min(spectrum.length - 6, Math.round(20000 / binRes));
        for (var bin = startBin; bin < endBin; bin++) {
            var freq = bin * binRes;
            if (isHumHarmonic(freq)) continue;
            var val = spectrum[bin];
            var neighbors = (avgRange(spectrum, bin - 6, bin - 3) + avgRange(spectrum, bin + 3, bin + 6)) / 2;
            var above = val - neighbors;
            if (above > maxPeak) maxPeak = above;
        }
        return maxPeak;
    }

    var SEVERITY_RANK = { strong: 3, moderate: 2, mild: 1 };
    /* When several issues coexist, this order breaks ties (most actionable first). */
    var TYPE_PRIORITY = ['hum50', 'hum60', 'emi', 'hiss', 'rumble', 'crackle'];

    /*
     * classify(spectrumDb, opts) → { issues:[{type,severity,val}], primary }
     *   spectrumDb : Float32Array — averaged getFloatFrequencyData (dB)
     *   opts.sampleRate, opts.fftSize : to derive binRes
     *   opts.crestFactors : array of per-window peak/rms ratios (for crackle)
     * Thresholds identical to mic-noise-test's detectNoiseTypes().
     */
    function classify(spectrumDb, opts) {
        opts = opts || {};
        var issues = [];
        if (!spectrumDb || !spectrumDb.length || !opts.sampleRate || !opts.fftSize) {
            return { issues: issues, primary: 'clean' };
        }
        var binRes = opts.sampleRate / opts.fftSize;

        var hum50 = detectHum(spectrumDb, binRes, 50);
        var hum60 = detectHum(spectrumDb, binRes, 60);
        if (hum50 > 15) issues.push({ type: 'hum50', severity: hum50 > 25 ? 'strong' : hum50 > 18 ? 'moderate' : 'mild', val: hum50 });
        if (hum60 > 15) issues.push({ type: 'hum60', severity: hum60 > 25 ? 'strong' : hum60 > 18 ? 'moderate' : 'mild', val: hum60 });

        var hiss = detectHiss(spectrumDb, binRes);
        if (hiss > -10) issues.push({ type: 'hiss', severity: hiss > 0 ? 'strong' : hiss > -5 ? 'moderate' : 'mild', val: hiss });

        var crest = opts.crestFactors || [];
        if (crest.length > 0) {
            var maxCrest = Math.max.apply(null, crest);
            var highCrestCount = crest.filter(function (c) { return c > 8; }).length;
            if (highCrestCount >= 3) issues.push({ type: 'crackle', severity: maxCrest > 15 ? 'strong' : 'moderate', val: maxCrest });
        }

        var rumble = detectRumble(spectrumDb, binRes);
        if (rumble > 12) issues.push({ type: 'rumble', severity: rumble > 20 ? 'strong' : 'moderate', val: rumble });

        var emi = detectEmi(spectrumDb, binRes);
        if (emi > 18) issues.push({ type: 'emi', severity: emi > 28 ? 'strong' : 'moderate', val: emi });

        /* Primary issue = strongest severity, then TYPE_PRIORITY order. */
        var primary = 'clean';
        if (issues.length) {
            var best = issues.slice().sort(function (a, b) {
                var d = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
                if (d !== 0) return d;
                return TYPE_PRIORITY.indexOf(a.type) - TYPE_PRIORITY.indexOf(b.type);
            })[0];
            primary = best.type;
        }
        return { issues: issues, primary: primary };
    }

    /* Median (default) or arbitrary percentile of an array of linear RMS values,
       returned in dBFS. Robust to brief transients (a cough moves only the top
       percentiles, not the median). pct in [0,1]. */
    function robustFloorDb(rmsValues, pct) {
        if (!rmsValues || !rmsValues.length) return -100;
        if (pct == null) pct = 0.5;
        var sorted = rmsValues.slice().sort(function (a, b) { return a - b; });
        var idx = Math.min(sorted.length - 1, Math.max(0, Math.floor(sorted.length * pct)));
        var rms = sorted[idx] || 1e-10;
        return rms < 1e-10 ? -100 : 20 * Math.log10(rms);
    }

    var API = {
        classify: classify,
        robustFloorDb: robustFloorDb,
        detectHum: detectHum,
        detectHiss: detectHiss,
        detectRumble: detectRumble,
        detectEmi: detectEmi,
        isHumHarmonic: isHumHarmonic,
        avgRange: avgRange
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) root.NoiseClassifier = API;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
